<template>
  <div>
    <confirmation-dialog
      v-model="showInactiveConfirmation"
      @confirm="saveClient(true)"
    >
      Are you sure you want to deactivate this client? All questionnaires from
      this client’s entities will be locked, and the template schedulers
      removed.
    </confirmation-dialog>
    <v-dialog
      width="800"
      v-model="opened"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      persistent
      @keydown.esc="$emit('update:opened', false)"
    >
      <v-card flat v-if="selectedClient">
        <v-card-title>
          <span class="title">Edit Client Information</span>
        </v-card-title>

        <v-card-text>
          <v-alert
            v-if="get(selectedClient, 'preferences.has_temp_email', false)"
            class="text-xs-left mb-3"
            type="info"
            :value="true"
            outline
          >
            User without contact email.
            <a href="#" @click.prevent.stop="focusEmailInput()">Add an email</a>
          </v-alert>
          <v-layout column>
            <v-layout wrap class="text-xs-left">
              <v-flex xs4 class="px-2">
                <div>First Name</div>
                <v-text-field
                  name="first-name"
                  placeholder="Jack"
                  type="text"
                  v-model="selectedClient.fname"
                  required
                  :rules="rules.presence"
                  solo
                  class="default-input ph-no-capture"
                />
              </v-flex>
              <v-flex xs4 class="px-2">
                <div>Last Name</div>
                <v-text-field
                  name="last-name"
                  placeholder="Smith"
                  type="text"
                  v-model="selectedClient.lname"
                  required
                  :rules="rules.presence"
                  solo
                  class="default-input ph-no-capture"
                />
              </v-flex>
              <v-flex xs4 class="px-2">
                <div>Preferred Name</div>
                <v-text-field
                  name="preferred-name"
                  placeholder="Johnny"
                  type="text"
                  v-model="selectedClient.preferred_name"
                  solo
                  class="default-input ph-no-capture"
                />
              </v-flex>
              <v-flex xs4 class="px-2">
                <div>Email</div>
                <v-text-field
                  type="text"
                  v-model="$v.selectedClient.email.$model"
                  :error="$v.selectedClient.email.$error"
                  :error-messages="
                    $v.selectedClient.email.$error
                      ? ['E-mail must be valid']
                      : []
                  "
                  solo
                  ref="userEmail"
                  placeholder="Email Address"
                  class="default-input ph-no-capture"
                />
              </v-flex>
              <v-flex xs4 class="px-2">
                <div>Phone number</div>
                <v-text-field
                  name="phone-number"
                  type="phone"
                  mask="phone"
                  placeholder="(123) 456 - 7890"
                  v-model="selectedClient.phone_number"
                  solo
                  class="default-input ph-no-capture"
                />
              </v-flex>
              <v-flex xs4 class="px-2">
                <div>Work Phone number</div>
                <v-layout>
                  <v-flex xs8 class="pr-1">
                    <v-text-field
                      name="phone-number"
                      type="phone"
                      mask="phone"
                      placeholder="Optional"
                      v-model="selectedClient.work_phone_number"
                      solo
                      class="default-input ph-no-capture"
                    />
                  </v-flex>
                  <v-flex xs4 class="pl-1">
                    <v-text-field
                      placeholder="Ext."
                      v-model="selectedClient.work_phone_extension"
                      solo
                      class="default-input"
                    />
                  </v-flex>
                </v-layout>
              </v-flex>
              <v-flex xs4 class="px-2">
                <div>Contact Method</div>
                <v-select
                  class="default-input"
                  solo
                  item-value="value"
                  item-text="label"
                  placeholder="Leave blank to let client decide"
                  :items="preferredContactMethods"
                  v-model="selectedClient.preferred_contact_method"
                />
              </v-flex>
              <v-flex xs5 class="px-2">
                <div class="text-xs-left">Time Zone</div>
                <v-select
                  v-model="selectedClient.time_zone"
                  solo
                  :items="timeZones"
                  class="default-input"
                />
              </v-flex>
              <v-flex
                xs12
                v-if="currentUser.overview_setting.use_contact_tags"
                class="px-2"
              >
                <div>Client Tags</div>
                <v-autocomplete
                  class="default-input"
                  :items="firmContactTags"
                  v-model="selectedTags"
                  placeholder="None"
                  item-text="name"
                  item-value="name"
                  solo
                  chips
                  small-chips
                  multiple
                />
              </v-flex>
              <template v-if="allowGroupChanges">
                <v-flex xs12 class="px-2">
                  <v-layout class="text-xs-left"> Assigned Group </v-layout>
                  <v-select
                    type="text"
                    :items="currentFirm.firm_groups"
                    placeholder="Select group"
                    v-model="userGroupId"
                    solo
                    class="default-input"
                    item-text="name"
                    item-value="id"
                    required
                    hide-details
                  />
                </v-flex>
                <v-flex v-if="showAssignUserSelect" xs12 class="px-2">
                  <v-alert
                    class="text-xs-left"
                    type="info"
                    :value="true"
                    outline
                  >
                    Assign to an admin user or a user within the new assigned
                    group.
                  </v-alert>
                  <v-layout class="text-xs-left"> Assigned To </v-layout>
                  <v-select
                    class="default-input body-1"
                    hide-details
                    solo
                    :items="filteredFirmUsers"
                    v-model="newAssignedUserId"
                    placeholder="Select a user"
                    item-text="name"
                    item-value="id"
                  />
                </v-flex>
              </template>
            </v-layout>
            <v-layout
              class="text-xs-left mb-4"
              v-if="isAdminOrFull && useFirmRoles"
            >
              <v-flex xs6 class="px-2">
                <div>Preparer</div>
                <v-autocomplete
                  class="default-input"
                  hide-details
                  solo
                  :items="firmUsersOptionsForUseFirmRoles"
                  v-model="selectedClient.cpa_id"
                  placeholder="No Preparer Assigned"
                  item-text="name"
                  item-value="id"
                />
              </v-flex>
              <v-flex xs6 class="px-2">
                <div>Client Manager</div>
                <v-autocomplete
                  class="default-input"
                  hide-details
                  solo
                  :items="firmUsersOptionsForUseFirmRoles"
                  v-model="selectedClient.client_manager_id"
                  placeholder="No Client Manager Assigned"
                  item-text="name"
                  item-value="id"
                />
              </v-flex>
            </v-layout>

            <v-layout class="text-xs-left mb-4">
              <v-flex xs12 class="px-2">
                <div>Account Settings</div>

                <v-layout class="mt-2">
                  <v-switch
                    slot="activator"
                    :input-value="!selectedClient.paying"
                    @change="selectedClient.paying = !$event"
                    hide-details
                    class="default-input my-0 mr-1"
                  />
                  <v-layout class="pl-2" column justify-center>
                    <div class="option-title">Deactivate account</div>
                    <div class="option-description">
                      The client will not be able to log in or receive
                      notifications. The client will no longer have access to
                      any entities.
                    </div>
                  </v-layout>
                </v-layout>

                <v-layout class="mt-2">
                  <v-switch
                    slot="activator"
                    v-model="selectedClient.readonly_access"
                    hide-details
                    class="default-input my-0 mr-1"
                    :disabled="!selectedClient.paying"
                  />
                  <v-layout class="pl-2" column justify-center>
                    <div class="option-title">Make account read-only</div>
                    <div class="option-description">
                      The client can still log in to access existing documents,
                      but they will not be able to upload new documents,
                      complete questionnaires or send messages.
                    </div>
                  </v-layout>
                </v-layout>
              </v-flex>
            </v-layout>
          </v-layout>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            class="primary text-none"
            :disabled="!clientSavable"
            :loading="saving"
            @click="
              clientMarkedInactive ? requestConfirmation() : saveClient(false)
            "
          >
            Save
          </v-btn>
          <v-btn
            class="error--text text-none"
            flat
            @click="$emit('update:opened', false)"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import API from '@/api';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import { mapState, mapGetters } from 'vuex';
import {
  timeZones,
  rules,
  yearOptions,
  preferredContactMethods,
  emailFormatter,
} from '@/constants/variables';
import ConfirmationDialog from '../ConfirmationDialog';
import { email } from 'vuelidate/lib/validators';
import FirmPreferenceMixins from '@/mixins/FirmPreferenceMixins';

export default {
  props: {
    opened: {
      type: Boolean,
    },
    selectedClient: {
      type: Object,
    },
    firmContactTags: {
      type: Array,
    },
    firmUsers: {
      type: Array,
    },
  },
  components: {
    ConfirmationDialog,
  },
  mixins: [FirmPreferenceMixins],
  data() {
    return {
      timeZones,
      rules,
      yearOptions,
      preferredContactMethods,
      showInactiveConfirmation: false,
      userWasActive: false,
      saving: false,
      userGroupId: null,
      newAssignedUserId: null,
      selectedTags: [],
    };
  },
  watch: {
    opened(newVal) {
      if (newVal) {
        this.initForm();
      }
    },
  },
  computed: {
    ...mapState('companies', ['companies']),
    ...mapGetters({
      currentUser: 'user',
      defaultCompany: 'defaultCompany',
      currentFirm: 'currentFirm',
    }),
    isAdminOrFull() {
      return (
        this.currentUser.firm_access === 'partner' ||
        this.currentUser.firm_access === 'full'
      );
    },
    clientSavable() {
      return (
        ((get(this.selectedClient, 'preferences.has_temp_email', false) &&
          !this.selectedClient.email) ||
          (this.selectedClient.email &&
            !this.$v.selectedClient.email.$error)) &&
        this.selectedClient.fname &&
        this.selectedClient.lname
      );
    },
    showAssignUserSelect() {
      return (
        this.userGroupId !== get(this.selectedClient, 'firm_group.id', null)
      );
    },
    filteredFirmUsers() {
      return this.allowGroupChanges
        ? this.firmUsers.filter(
            user =>
              user.access === 'partner' ||
              (user.group && user.group.id === this.userGroupId),
          )
        : [];
    },
    firmUsersOptionsForUseFirmRoles() {
      return [{ id: null, name: 'None' }, ...this.firmUsers];
    },
    clientMarkedInactive() {
      return (
        this.selectedClient && !this.selectedClient.paying && this.userWasActive
      );
    },
  },
  validations() {
    let validations = {
      selectedClient: {
        email: {
          email: val => email(emailFormatter(val)),
        },
      },
    };

    return validations;
  },
  methods: {
    get,
    isEmpty,
    moment,
    focusEmailInput() {
      this.$refs.userEmail.$refs.input.focus();
    },
    initForm() {
      this.userWasActive = this.selectedClient.paying;
      this.userGroupId = get(this.selectedClient, 'firm_group.id', null);
      this.selectedTags = this.selectedClient.contact_tags
        ? this.selectedClient.contact_tags.map(c => c.name)
        : [];
      this.newAssignedUserId = null;
    },
    requestConfirmation() {
      this.showInactiveConfirmation = true;
    },
    async saveClient(reloadQuestionnaires) {
      if (
        this.selectedClient &&
        this.selectedClient.preferred_sign_in_method == 'sms' &&
        this.selectedClient.phone_number == ''
      ) {
        this.$store.dispatch('application/setErrorNotification', {
          message:
            'The client has set their login in method through SMS verification. If you would like to disable SMS reminder for this client, please change their contact method.',
        });
        return;
      }
      this.saving = true;
      this.showInactiveConfirmation = false;
      const {
        phone_number,
        fname,
        lname,
        email,
        paying,
        preferred_name,
        preferred_contact_method,
        time_zone,
        work_phone_number,
        work_phone_extension,
        cpa_id,
        client_manager_id,
        readonly_access,
      } = this.selectedClient;

      try {
        const response = await API.user.updateUser({
          id: this.selectedClient.id,
          fname,
          lname,
          email,
          paying,
          phone_number,
          preferred_name,
          preferred_contact_method,
          work_phone_number,
          work_phone_extension,
          cpa_id,
          time_zone,
          client_manager_id,
          readonly_access,
          group_id: this.userGroupId,
        });

        if (
          this.allowGroupChanges &&
          this.showAssignUserSelect &&
          this.newAssignedUserId
        ) {
          await API.advisor.updateAssignments({
            ids: [this.selectedClient.id],
            advisorId: this.newAssignedUserId,
          });
        }

        if (response.data.error) {
          console.log(response.data.error);
          this.$store.dispatch('application/setErrorNotification', {
            message: response.data.error,
          });
        } else {
          await this.updateTags();

          this.$store.dispatch('setDefaultCompany', {
            companyId: this.defaultCompany.id,
          });
          this.$store.dispatch('application/setSuccessNotification', {
            message: `Information has been saved successfully`,
          });
          this.$emit('update:opened', false);
          this.$emit('refresh');

          if (reloadQuestionnaires) {
            this.$emit('refresh-questionnaires');
          }
        }
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Something went wrong while saving information.',
        });
      } finally {
        this.saving = false;
      }
    },
    async updateTags() {
      try {
        const response = await API.user.updateTags({
          userId: this.selectedClient.id,
          contactTags: this.selectedTags,
        });
        if (response.data.error) {
          console.log(response.data.error);
          this.$store.dispatch('application/setErrorNotification', {
            message: response.data.error,
          });
        }
        this.$store.dispatch('companies/getCompanies');
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Something went wrong while saving tags.',
        });
      }
    },
  },
};
</script>

<style lang="postcss" scoped>
.option-title {
  font-size: 14px;
}

.option-description {
  font-size: 12px;
  color: #545454;
}
</style>
